import { Card, Chip, Collapse, MenuItem, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import { useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { IWorkflow } from "@/interfaces/workflow.interface";
import { WorkflowMessages } from "@/messages/workflow/workflow.messages";
import { FilterType, IFilter } from "@/types/filter.types";
import { WorkflowTypes } from "@/types/workflow.types";

type WorkflowFiltersFormProps = {
  messages: WorkflowMessages;
  temporaryFilters: IFilter<IWorkflow>[];
  deleteTemporaryFilter: (index: number) => void;
  agentTypeOptions?: Array<{ value: number; label: string }>;
  resolveWorkflowTypeLabel?: (workflowTypeId: number) => string;
};

interface FilterFormData {
  agentName: string;
  agentType: string;
  description: string;
  status: string;
}

export default function WorkflowFiltersForm({
  messages,
  temporaryFilters,
  deleteTemporaryFilter,
  agentTypeOptions,
  resolveWorkflowTypeLabel,
}: WorkflowFiltersFormProps) {
  const { setValues, submitForm } = useFormikContext<IFilter<IWorkflow>[]>();

  const {
    moreFiltersButton,
    lessFiltersButton,
    clearFiltersButton,
    filterButton,
    nameLabel,
    agentTypeLabel,
    descriptionLabel,
    statusLabel,
    activeOption,
    inactiveOption,
  } = messages;

  const [expanded, setExpanded] = useState(false);
  const [formData, setFormData] = useState<FilterFormData>({
    agentName: "",
    agentType: "",
    description: "",
    status: "",
  });

  const updateFormData = (field: keyof FilterFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getWorkflowTypeId = (input: string): number | null => {
    const value = input.toLowerCase();

    if (/^\d+$/.test(input)) return parseInt(input);
    if (value.includes("start") || value.includes("iniciar")) return WorkflowTypes.StartFlow;
    if (value.includes("order") || value.includes("pedido") || value.includes("status"))
      return WorkflowTypes.OrderStatus;
    if (value.includes("shop") || value.includes("shopping")) return WorkflowTypes.Shopping;

    return null;
  };

  const handleClearFilters = () => {
    setFormData({
      agentName: "",
      agentType: "",
      description: "",
      status: "",
    });

    temporaryFilters.forEach((_, index) => deleteTemporaryFilter(index));

    setValues([]);
  };

  const handleApplyFilters = () => {
    const newFilters: IFilter<IWorkflow>[] = [];

    if (formData.agentName.trim()) {
      newFilters.push({
        property: "name" as keyof IWorkflow,
        value: formData.agentName.trim(),
        filterType: FilterType.Contains,
        label: nameLabel,
      });
    }

    if (formData.agentType.trim()) {
      if (agentTypeOptions && agentTypeOptions.length > 0) {
        const workflowTypeId = Number(formData.agentType);
        if (!Number.isNaN(workflowTypeId)) {
          newFilters.push({
            property: "workflowTypeId" as keyof IWorkflow,
            value: workflowTypeId,
            filterType: FilterType.Equals,
            label: agentTypeLabel,
          });
        }
      } else {
        const workflowTypeId = getWorkflowTypeId(formData.agentType.trim());
        if (workflowTypeId !== null) {
          newFilters.push({
            property: "workflowTypeId" as keyof IWorkflow,
            value: workflowTypeId,
            filterType: FilterType.Equals,
            label: agentTypeLabel,
          });
        }
      }
    }

    if (formData.description.trim()) {
      newFilters.push({
        property: "description" as keyof IWorkflow,
        value: formData.description.trim(),
        filterType: FilterType.Contains,
        label: descriptionLabel,
      });
    }

    if (formData.status) {
      const statusValue = formData.status === "ativo";
      newFilters.push({
        property: "active" as keyof IWorkflow,
        value: statusValue,
        filterType: FilterType.Equals,
        label: statusLabel,
      });
    }

    setValues(newFilters);
    submitForm();
  };

  const handleRemoveTemporaryFilter = (index: number) => {
    deleteTemporaryFilter(index);
  };

  const formatFilterValue = (filter: IFilter<IWorkflow>) => {
    switch (filter.property) {
      case "workflowTypeId":
        return formatWorkflowTypeId(Number(filter.value));
      case "active":
        return filter.value === true ? messages.statusTrueOption : messages.statusFalseOption;
      default:
        return filter.value;
    }
  };

  const formatWorkflowTypeId = (value: number) => {
    const resolved = resolveWorkflowTypeLabel?.(value);
    if (resolved !== undefined && resolved !== "") return resolved;

    switch (value) {
      case WorkflowTypes.StartFlow:
        return messages.startFlow;
      case WorkflowTypes.OrderStatus:
        return messages.orderStatus;
      case WorkflowTypes.Shopping:
        return messages.shopping;
      default:
        return "";
    }
  };

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={2} alignItems="stretch" wrap="wrap">
          <Grid size={{ lg: 6, md: 6, sm: 6, xs: 12 }}>
            <TextField
              fullWidth
              label={nameLabel}
              variant="outlined"
              value={formData.agentName}
              onChange={(e) => updateFormData("agentName", e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid>

          <Grid size={{ lg: 6, md: 6, sm: 6, xs: 12 }}>
            {agentTypeOptions && agentTypeOptions.length > 0 ? (
              <TextField
                select
                fullWidth
                label={agentTypeLabel}
                variant="outlined"
                value={formData.agentType}
                onChange={(e) => updateFormData("agentType", e.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                }}
              >
                {agentTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                fullWidth
                label={agentTypeLabel}
                variant="outlined"
                value={formData.agentType}
                onChange={(e) => updateFormData("agentType", e.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                }}
              />
            )}
          </Grid>

          <Grid size={12}>
            <Collapse in={expanded} unmountOnExit>
              <Grid container spacing={2}>
                <Grid size={{ lg: 8, md: 8, sm: 6, xs: 12 }}>
                  <TextField
                    fullWidth
                    label={descriptionLabel}
                    variant="outlined"
                    value={formData.description}
                    onChange={(e) => updateFormData("description", e.target.value)}
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  />
                </Grid>

                <Grid size={{ lg: 4, md: 4, sm: 6, xs: 12 }}>
                  <TextField
                    select
                    fullWidth
                    label={statusLabel}
                    variant="outlined"
                    value={formData.status}
                    onChange={(e) => updateFormData("status", e.target.value)}
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  >
                    <MenuItem value="ativo">{activeOption}</MenuItem>
                    <MenuItem value="desativado">{inactiveOption}</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Collapse>

            <Grid container spacing={2} mt={expanded ? 2 : 0}>
              <Grid size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={() => setExpanded((prev) => !prev)}
                  fullWidth
                >
                  {expanded ? lessFiltersButton : moreFiltersButton}
                </MDButton>
              </Grid>

              <Grid size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                <MDButton variant="gradient" color="light" onClick={handleClearFilters} fullWidth>
                  {clearFiltersButton}
                </MDButton>
              </Grid>

              <Grid size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                <MDButton variant="gradient" color="dark" onClick={handleApplyFilters} fullWidth>
                  {filterButton}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {temporaryFilters.length > 0 && (
          <Grid container spacing={2} size={12} mt={2}>
            {temporaryFilters.map((filter, index) => (
              <Grid key={index}>
                <Chip
                  label={`${filter.label}: ${formatFilterValue(filter)}`}
                  onDelete={() => handleRemoveTemporaryFilter(index)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </MDBox>
    </Card>
  );
}
